import { Signal } from "signals";
import Components from "slotworks/components/components";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import CancelGroup from "slotworks/utils/contracts/cancel-group";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Point from "slotworks/utils/geom/point";
import ReelComponent from "../../reel-component";
import AbstractReelTransition from "../abstract-reel-transition";
import ReelDropper from "./reel-dropper";

export class DropReelTransition extends AbstractReelTransition {

    public static START_GAPS: number[] = [250, 175, 0, 0, 175, 250];
    public static STOP_GAPS: number[] = [0, 0, 0, 0, 0, 0];

    public onReelDrop: Signal = new Signal();

    private droppers: ReelDropper[];
    private anticipatingReels: number[] = [];
    private skipping: boolean;
    private skipPossible: boolean;
    private cancelGroup: CancelGroup = new CancelGroup();

    public init(reels: ReelComponent[], stops: number[], reelset: string[][]): void {
        super.init(reels, stops, reelset);
        this.reels = reels;
        this.droppers = [];

        for (let i = 0; i < reels.length; i++) {
            const reel = reels[i];
            const dropper: ReelDropper = new ReelDropper(reel, stops[i], reelset[i]);
            this.droppers.push(dropper);
            dropper.onDrop.add((index: number) => this.reelDroppedHandler(index));
            dropper.onLand.add((index: number) => this.reelLandedHandler(index));
            dropper.onAnticipationStart.add((index: number) => this.anticipatingStartHandler(index));
            dropper.onAnticipationEnd.add((index: number) => this.anticipatingEndHandler(index));
        }
    }

    public hold(reelIndexes: number[], hold: boolean = true): void {
        reelIndexes.forEach((index: number) => {
            if (this.droppers[index]) {
                this.droppers[index].hold(hold);
            }
        });
    }

    public holdSymbol(points: Point[], hold: boolean = true): void {
        points.forEach((point: Point) => this.holdSymbolAt(point.x, point.y, hold));
    }

    public start(reelset?: string[][], jumpStart: boolean = false, quickSpin: boolean = false): Contract<void> {
        this.anticipatingReels = [];
        this.skipPossible = true;
        this.skipping = false;

        Services.sound.event(SoundEvent.reel_transition_start);
        Components.matrix.resetSymbols();

        const startMethods: Array<() => Contract<void>> = [];
        this.droppers.forEach((dropper: ReelDropper, i: number) => startMethods.push(() => dropper.start(DropReelTransition.START_GAPS[i], jumpStart, quickSpin)));

        return this.cancelGroup.parallel(startMethods);
    }

    public stop(spinRecord: SpinRecord, quickSpin: boolean = false): Contract<void> {
        const stopMethods: Array<() => Contract<void>> = [];
        this.droppers.forEach((dropper: ReelDropper, i: number) => {
            stopMethods.push(() => dropper.stop(spinRecord, quickSpin ? 0 : DropReelTransition.STOP_GAPS[i], quickSpin));
        });
        return this.cancelGroup.parallel(stopMethods);
    }

    public skip(spinRecord: SpinRecord): Contract<void> {
        this.cancelGroup.cancel();
        const skipContracts: Array<() => Contract<void>> = [];
        this.droppers.forEach((dropper: ReelDropper, i: number) => skipContracts.push(() => dropper.skip(spinRecord)));
        return new Parallel(skipContracts);
    }

    public canSkip() {
        return this.skipPossible;
    }

    public reelLandedHandler(reelIndex: number): void {
        this.onReelLand.dispatch(reelIndex);
    }

    public reelDroppedHandler(reelIndex: number): void {
        this.onReelDrop.dispatch(reelIndex);
    }

    public getSkipping(): boolean {
        return this.skipping;
    }

    private anticipatingStartHandler(index: number): void {
        if (this.anticipatingReels.length === 0) {
            this.onAnticipationStart.dispatch();
        }
        this.anticipatingReels.push(index);
    }

    private anticipatingEndHandler(index: number): void {
        this.anticipatingReels.pop();
        if (this.anticipatingReels.length === 0) {
            this.onAnticipationEnd.dispatch();
        }
    }

    private holdSymbolAt(reelIndex: number, symbolIndex: number, hold: boolean = true): void {
        this.droppers[reelIndex].holdSymbol(symbolIndex, hold);
    }
}
